import {
    Component,
    ChangeDetectionStrategy,
    Input,
    OnChanges,
    ChangeDetectorRef,
    Output,
    EventEmitter,
} from '@angular/core';
import { PickChargingStationRow, StationInfo } from 'api/charging_stations.api';
import { GlobalSearchStationResult } from '@kempoweroyj/ngx-search-api-client';
import { CustomerPrivacyService } from 'layout/customer-privacy/customer-privacy.service';
import { combineLatest, distinctUntilChanged, map, Observable, of, Subject, switchMap } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { RouterLink } from '@angular/router';
import { AuthService } from 'providers/auth.service';

@Component({
    selector: 'ce-station-badge',
    templateUrl: './station-badge.component.html',
    styleUrls: ['./station-badge.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
@UntilDestroy()
export class StationBadgeComponent implements OnChanges {
    @Input() headerStyle = false;
    @Input() station: StationInfo | PickChargingStationRow;
    @Input() globalSearchStation: GlobalSearchStationResult;
    /**
     * If set and length greater than 1, display general information e.g number of stations selected.
     */
    @Input() stations: Partial<StationInfo | PickChargingStationRow>[];
    @Output() badgeClicked = new EventEmitter<void>();

    /**
     * readonly property to display number of stations
     */
    numberOfStations: number = 0;

    /**
     * multiple stations: null
     * single station: link to station view
     */
    linkToStation: RouterLink['routerLink'] | null;
    /**
     * multiple stations: <station name>, <location name>
     * single station: null
     */
    badgeTooltip$: Observable<string>;

    /**
     * Description for the badge
     */
    description$: Observable<string>;

    _refreshRequested$: Subject<null> = new Subject<null>();

    constructor(
        private readonly cdRef: ChangeDetectorRef,
        private readonly authService: AuthService,
        public customerPrivacy: CustomerPrivacyService
    ) {}

    ngOnInit() {
        // Init badge tooltip
        this.badgeTooltip$ = this._refreshRequested$.asObservable().pipe(
            switchMap(() => {
                // if multiple stations, generate tooltip
                if (this.numberOfStations > 1) {
                    return of(this.generateTooltip());
                }
                // else return null
                return of(null);
            })
        );

        this.description$ = combineLatest([
            this.authService.profile$,
            this.authService.hasAccessTo$('salesOrders.access'),
        ]).pipe(
            map(([, hasAccessSalesOrder]) => {
                if (this.numberOfStations > 1) {
                    return null;
                }
                const location = this.station?.location?.name || this.globalSearchStation?.location?.locationName;
                const description = this.customerPrivacy.transform(location, 'location');
                const orderNumbers = this.globalSearchStation?.orderNumbers;

                if (orderNumbers && hasAccessSalesOrder) {
                    return description ? `${description} - ${orderNumbers}` : orderNumbers;
                }
                return description || '';
            })
        );

        // Listen customer privacy changes
        this.customerPrivacy.enabled$.pipe(distinctUntilChanged(), untilDestroyed(this)).subscribe(() => {
            this._refreshRequested$.next(null);
        });
    }

    ngOnChanges() {
        // update number of stations
        if (this.stations?.length) {
            this.numberOfStations = this.stations.length;
        } else if (this.station || this.globalSearchStation) {
            this.numberOfStations = 1;
        }

        // update link
        this.linkToStation =
            this.numberOfStations > 1
                ? null
                : ['/locations', 'station', this.station?.stationId || this.globalSearchStation?.stationId];

        // refresh tooltip
        this._refreshRequested$.next(null);

        this.cdRef.markForCheck();
    }
    generateTooltip() {
        let tooltip = '';

        for (const s of this.stations) {
            const _name = s.name || s.stationId;
            const name = this.customerPrivacy.transform(_name, 'station');
            tooltip += `${name}`;

            const _loc = s.location?.name || null;
            if (_loc) {
                const loc = this.customerPrivacy.transform(_loc, 'location');
                tooltip += `, ${loc}\n`;
            } else {
                tooltip += `\n`;
            }
        }

        return tooltip;
    }
}
